/**
 * Sprint 4SED.C — TravasoVeicoliWizard
 *
 * Wizard riusabile per il travaso bulk di veicoli a stock di una azienda
 * da una sede origine verso una sede destinazione + deposito.
 *
 * Step 1 — Selezione veicoli (GET /sites/{site_id}/transferable-vehicles)
 * Step 2 — Destinazione (sede attiva dell'azienda diversa dall'origine
 *          + deposito tramite DepositoPicker)
 * Conferma — POST /sites/{site_id}/transfer-vehicles → mostra remaining_in_origin
 *
 * Props:
 *  - open            : bool, apertura del dialog
 *  - onClose()       : callback chiusura (annulla / x)
 *  - originSiteId    : sede origine (string)
 *  - originSiteName  : nome sede origine (per UI)
 *  - companyId       : azienda di cui spostare i veicoli (string)
 *  - companyName     : nome azienda (per UI)
 *  - context         : "slega_azienda" | "disattiva_sede" | "standalone"
 *                       (determina la CTA finale a remaining=0)
 *  - onAllTransferred() : callback quando remaining=0 e l'utente clicca la CTA
 *                         finale ("Slega azienda ora" / "Disattiva sede ora")
 *  - onAnyTransferred() : callback ad ogni travaso riuscito (per refresh elenchi)
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import DepositoPicker from "@/components/DepositoPicker";
import TipoCaricoBadge from "@/components/TipoCaricoBadge";
import { ArrowRight, ArrowLeft, Truck, CheckCircle2, AlertTriangle, Warehouse, Boxes } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  VIRTUAL: "Virtuale", INCOMING: "In arrivo", AVAILABLE: "Disponibile",
};

export default function TravasoVeicoliWizard({
  open, onClose, originSiteId, originSiteName,
  companyId, companyName,
  context = "standalone",
  onAllTransferred, onAnyTransferred,
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [companySites, setCompanySites] = useState([]);
  const [destSiteId, setDestSiteId] = useState("");
  const [destDepositoId, setDestDepositoId] = useState(null);
  const [lastResult, setLastResult] = useState(null); // {transferred_count, remaining_in_origin}

  // Reset quando si apre/chiude
  useEffect(() => {
    if (!open) {
      setStep(1); setVehicles([]); setSelectedIds(new Set());
      setCompanySites([]); setDestSiteId(""); setDestDepositoId(null);
      setLastResult(null);
      return;
    }
    if (!originSiteId || !companyId) return;
    loadStep1();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, originSiteId, companyId]);

  const loadStep1 = async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/sites/${originSiteId}/transferable-vehicles`,
        { params: { company_id: companyId } });
      const items = r.data?.items || [];
      setVehicles(items);
      // Default: tutti selezionati
      setSelectedIds(new Set(items.map((v) => v.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento veicoli");
    } finally {
      setLoading(false);
    }
  };

  const loadStep2 = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/companies/${companyId}/sites`);
      // Solo sedi attive e diverse dall'origine
      const filtered = (r.data || []).filter(
        (s) => s.id !== originSiteId && s.attiva !== false);
      setCompanySites(filtered);
      if (filtered.length === 0) {
        toast.error("Nessuna sede di destinazione disponibile per questa azienda. Creane una prima.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento sedi");
    } finally {
      setLoading(false);
    }
  };

  const toggleId = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleAll = () => {
    if (selectedIds.size === vehicles.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(vehicles.map((v) => v.id)));
  };

  const goStep2 = async () => {
    if (selectedIds.size === 0) {
      toast.error("Seleziona almeno un veicolo"); return;
    }
    await loadStep2();
    setStep(2);
  };

  const goBackStep1 = () => {
    setStep(1);
    setDestSiteId(""); setDestDepositoId(null);
  };

  const confirm = async () => {
    if (!destSiteId) { toast.error("Seleziona la sede di destinazione"); return; }
    setTransferring(true);
    try {
      const payload = {
        company_id: companyId,
        vehicle_ids: Array.from(selectedIds),
        dest_site_id: destSiteId,
        dest_deposito_id: destDepositoId || null,
      };
      const r = await api.post(
        `/sites/${originSiteId}/transfer-vehicles`, payload);
      const { transferred_count, remaining_in_origin } = r.data;
      setLastResult({ transferred_count, remaining_in_origin });
      toast.success(`Trasferiti ${transferred_count} veicoli`);
      if (onAnyTransferred) onAnyTransferred();
      // Se restano altri veicoli, ricarica step 1 per consentire ulteriore travaso
      if (remaining_in_origin > 0) {
        await loadStep1();
        setStep(1);
      } else {
        setStep(3); // schermata finale "tutti trasferiti"
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object") {
        msg = detail.detail || detail.error || "Errore travaso";
      } else {
        msg = detail || "Errore travaso";
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setTransferring(false);
    }
  };

  const ctaFinalLabel = context === "slega_azienda"
    ? "Slega azienda ora"
    : context === "disattiva_sede" ? "Disattiva sede ora"
    : null;

  const allSelected = vehicles.length > 0 && selectedIds.size === vehicles.length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="travaso-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Travaso veicoli {step < 3 && <span className="text-sm font-normal text-slate-500">— Step {step}/2</span>}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium">{companyName || "Azienda"}</span>
            <ArrowRight className="inline h-3 w-3 mx-1 text-slate-400" />
            sposta i veicoli <strong>a stock</strong> dalla sede{" "}
            <span className="font-medium">{originSiteName || originSiteId}</span> verso un'altra sede.
          </DialogDescription>
        </DialogHeader>

        {/* ============ STEP 1: selezione veicoli ============ */}
        {step === 1 && (
          <div className="space-y-3">
            {lastResult && lastResult.remaining_in_origin > 0 && (
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-900 px-3 py-2 rounded-md text-[12px]">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  Trasferiti {lastResult.transferred_count} veicoli. Ne restano{" "}
                  <strong>{lastResult.remaining_in_origin}</strong> a stock. Puoi continuare con un'altra destinazione.
                </div>
              </div>
            )}
            {loading ? (
              <div className="p-6 text-center text-slate-400 text-sm italic">Caricamento veicoli…</div>
            ) : vehicles.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                Nessun veicolo a stock da trasferire per questa azienda in questa sede.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-[12px] text-slate-600">
                  <button
                    onClick={toggleAll}
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                    data-testid="travaso-select-all"
                  >
                    {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                  </button>
                  <span data-testid="travaso-selection-count">
                    Selezionati: <strong>{selectedIds.size}</strong> / {vehicles.length}
                  </span>
                </div>
                <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
                  <div className="max-h-[360px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left w-8"></th>
                          <th className="px-3 py-2 text-left">Targa / Telaio</th>
                          <th className="px-3 py-2 text-left">Marca / Modello</th>
                          <th className="px-3 py-2 text-left">Tipo carico</th>
                          <th className="px-3 py-2 text-left">Deposito origine</th>
                          <th className="px-3 py-2 text-left">Stato</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {vehicles.map((v) => {
                          const dep = v.deposito_origine;
                          const isSysDep = dep?.tipo === "SISTEMA";
                          return (
                          <tr
                            key={v.id}
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => toggleId(v.id)}
                            data-testid={`travaso-veh-row-${v.id}`}
                          >
                            <td className="px-3 py-2">
                              <Checkbox
                                checked={selectedIds.has(v.id)}
                                onCheckedChange={() => toggleId(v.id)}
                                data-testid={`travaso-veh-check-${v.id}`}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-[12px] text-slate-700">
                              {v.targa || v.telaio || "—"}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {[v.marca, v.modello].filter(Boolean).join(" ") || "—"}
                            </td>
                            <td className="px-3 py-2">
                              <TipoCaricoBadge value={v.tipo_carico} size="sm" />
                            </td>
                            <td className="px-3 py-2"
                                data-testid={`travaso-veh-dep-${v.id}`}>
                              {dep ? (
                                <span
                                  className={
                                    "inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border " +
                                    (isSysDep
                                      ? "bg-violet-50 border-violet-200 text-violet-800"
                                      : "bg-slate-50 border-slate-200 text-slate-700")
                                  }
                                  title={isSysDep
                                    ? "Deposito di SISTEMA (globale)"
                                    : "Deposito FISICO della sede"}
                                >
                                  {isSysDep
                                    ? <Boxes className="h-3 w-3" />
                                    : <Warehouse className="h-3 w-3" />}
                                  {dep.nome}
                                </span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                                {STATUS_LABEL[v.status] || v.status}
                              </span>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* ============ STEP 2: destinazione ============ */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="text-[12px] text-slate-600">
              Stai per trasferire <strong>{selectedIds.size}</strong> veicoli.
            </div>
            <div>
              <Label className="text-xs">Sede di destinazione *</Label>
              <Select value={destSiteId} onValueChange={(v) => {
                setDestSiteId(v);
                setDestDepositoId(null); // reset deposito se cambia sede
              }}>
                <SelectTrigger className="h-9 text-sm" data-testid="travaso-dest-site">
                  <SelectValue placeholder={
                    loading ? "Caricamento…" :
                    companySites.length === 0
                      ? "Nessuna sede disponibile"
                      : "— Seleziona sede destinazione —"} />
                </SelectTrigger>
                <SelectContent>
                  {companySites.map((s) => (
                    <SelectItem key={s.id} value={s.id}
                                data-testid={`travaso-dest-site-opt-${s.id}`}>
                      {s.nome} {s.indirizzo?.comune ? `— ${s.indirizzo.comune}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {companySites.length === 0 && !loading && (
                <p className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Crea prima una seconda sede attiva per l'azienda.
                </p>
              )}
            </div>
            <div>
              <DepositoPicker
                value={destDepositoId}
                onChange={setDestDepositoId}
                siteId={destSiteId || null}
                label="Deposito di destinazione *"
                testIdPrefix="travaso-dest-deposito"
              />
            </div>
          </div>
        )}

        {/* ============ STEP 3: tutti trasferiti ============ */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-3 rounded-md">
              <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium">Tutti i veicoli sono stati trasferiti.</div>
                <div className="text-[12px] mt-0.5">
                  Trasferiti complessivamente {lastResult?.transferred_count || 0} veicoli.
                  La sede di origine non ha più veicoli a stock di {companyName || "questa azienda"}.
                </div>
              </div>
            </div>
            {ctaFinalLabel && (
              <p className="text-[12px] text-slate-600">
                Puoi ora completare l'operazione che hai avviato.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={onClose}
                      disabled={transferring}
                      data-testid="travaso-cancel">
                Annulla
              </Button>
              <Button
                onClick={goStep2}
                disabled={selectedIds.size === 0 || loading || transferring}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="travaso-next"
              >
                Avanti <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={goBackStep1}
                      disabled={transferring}
                      data-testid="travaso-back">
                <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
              </Button>
              <Button
                onClick={confirm}
                disabled={!destSiteId || !destDepositoId || transferring}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="travaso-confirm"
              >
                {transferring ? "Trasferimento…" : "Conferma trasferimento"}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="outline" onClick={onClose}
                      data-testid="travaso-close">
                Chiudi
              </Button>
              {ctaFinalLabel && onAllTransferred && (
                <Button
                  onClick={() => { onAllTransferred(); onClose(); }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="travaso-cta-final"
                >
                  {ctaFinalLabel}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
